<template>
  <div class="js-text-overlay-timeline wt-text-overlay-timeline">
    <div class="ruler">
      <i
        v-for="(rulerItem, i) in ruler"
        class="-ruler-item"
        :key="i"
        :data-start-time="rulerItem.startTime"
        :data-end-time="rulerItem.endTime"
        :style="{ width: rulerItem.width + 'px' }"
      />
      <i
        v-for="(overlayMarker, i) in overlayTrackMarkers"
        class="-overlay-marker"
        :key="i + ruler.length"
        :style="{
          width: overlayMarker.width + 'px',
          left: overlayMarker.left + 'px',
        }"
      />
    </div>
    <div>
      <div
        class="wt-play-head"
        ref="playhead"
        :style="{ transform: 'translateX(' + playheadLeft + 'px)' }"
        data-tot-action="move-play-head"
      >
        <i />
      </div>

      <ul ref="ul" class="list-unstyled wt2-sg" :style="listStyle">
        <li
          class="-drag-shadow"
          v-show="shadow.show"
          :style="shadowStyle.outer"
        >
          <span class="-drag-vertically wticon wticon-reorder" />

          <div class="wt-timeline-handle" :style="shadowStyle.inner">
            <div class="wt-start-point ui-resizable-handle ui-resizable-w" />
            <div class="tot-inner">
              <div class="wt-timeline-caption wt2-text-n">
                <div class="inner" v-text="shadow.text" />
              </div>
            </div>
            <div class="wt-end-point ui-resizable-handle ui-resizable-e" />
          </div>
        </li>
        <li v-if="!textOverlays.length" class="wt-empty-timeline">
          <div />
        </li>

        <li
          v-for="textOverlay in textOverlays"
          :key="textOverlay.id"
          :class="getListItemClass(textOverlay)"
          :style="getListItemStyle(textOverlay)"
          :data-z-index="textOverlay.zIndex"
        >
          <span
            v-if="textOverlays.length > 1"
            title="Move up to bring to front, down to send back"
            class="-drag-vertically wticon wticon-reorder"
            data-tot-action="move-vertical"
            :data-textoverlay-id="textOverlay.id"
          />
          <div
            data-tot-action="move-horizontal"
            class="wt-timeline-handle js-timeline-handle"
            :style="getTotStyle(textOverlay)"
            :data-textoverlay-id="textOverlay.id"
            @click="selectTextOverlay(textOverlay)"
          >
            <div
              class="wt-start-point ui-resizable-handle ui-resizable-w"
              :data-textoverlay-id="textOverlay.id"
              data-tot-action="resize-left"
            />
            <div class="tot-inner">
              <div class="wt-timeline-caption wt2-text-n2">
                <div class="inner" v-text="textOverlay._text" />
              </div>
            </div>
            <div
              class="wt-end-point ui-resizable-handle ui-resizable-e"
              :data-textoverlay-id="textOverlay.id"
              data-tot-action="resize-right"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's Text Overlay Timeline (bottom bar)
 */
import app from 'app';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { mapGetters, mapMutations, mapState } from 'vuex';

export default {
  data: function () {
    return {
      ruler: [],
      overlayTrackMarkers: [],
      totStyles: {},
      snapPoints: [],
      totHeight: '35px',
      totMargin: '5px',
      snapThreshold: 10,
      minTotWidth: 27,
      shadow: {
        left: 0,
        text: '',
        width: 0,
        show: false,
        top: 0,
      },
      interaction: {
        mode: 'none',
        id: null,
        startX: 0,
        startY: 0,
        lastX: 0,
        direction: null,
        originalLeft: 0,
        originalWidth: 0,
      },
      activeTextOverlayId: null,
      totChangeEventEmitted: false,
    };
  },

  watch: {
    totStyles: {
      handler: function () {
        this.setStartAndEndTime();
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters('assetEditor', ['tmpAGD']),
    ...mapState('assetEditor', {
      overlayTracks: (state) => state.workModel.overlayTracks || [],
      displayDuration: (state) => state.workModel.displayDuration,
      assetEditorWidth: (state) => state.stageDimensions.assetEditorWidth,
      playheadLeft: (state) =>
        Math.round(state.timeInAsset * app.ASSET_EDITOR_WIDTH),
    }),

    horizontalAction() {
      return ['move-horizontal', 'resize-right', 'resize-left'].includes(
        this.interaction.mode
      );
    },

    verticalAction() {
      return this.interaction.mode === 'move-vertical';
    },

    listStyle() {
      return {
        position: 'relative',
        height:
          this.textOverlays.length * (this.totHeight + this.totMargin) -
          this.totMargin +
          'px',
      };
    },

    shadowStyle() {
      return {
        inner: {
          transform: `translateX(${this.shadow.left}px`,
          width: this.shadow.width + 'px',
        },
        outer: {
          transform: `translateY(${this.shadow.top}px)`,
        },
      };
    },

    textOverlays() {
      return this.tmpAGD.textOverlays || [];
    },

    currentTotStyle() {
      if (_.isEmpty(this.totStyles) || !this.interaction.id) {
        return null;
      }
      return this.totStyles[this.interaction.id];
    },
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setTimeInAsset',
      'setTimeInAssetToFirstTextOverlay',
      'playheadPositionToFirstTextOverlay',
      'playheadPosition',
      'setTmpPageState',
      'setTempTextOverlay',
      'enableMouse',
      'disableMouse',
    ]),

    getTextOverlayById(id) {
      if (!this.textOverlays.length) {
        return null;
      }
      return this.textOverlays.find((to) => to.id === id);
    },

    getTextOverlayByZindex(zIndex) {
      if (!this.textOverlays.length) {
        return null;
      }
      return this.textOverlays.find((to) => to.zIndex === zIndex);
    },

    getTextOverlayByZIndexRange(fromZIndex = 0, toZIndex = Infinity) {
      if (!this.textOverlays.length) {
        return null;
      }
      return this.textOverlays.filter(
        (t) => t.zIndex >= fromZIndex && t.zIndex <= toZIndex
      );
    },

    calcTotStyle(textOverlay) {
      return {
        left: Math.round(
          app.ASSET_EDITOR_WIDTH * textOverlay.animationStartTime
        ),
        width: Math.round(
          app.ASSET_EDITOR_WIDTH *
            (textOverlay.animationEndTime - textOverlay.animationStartTime)
        ),
      };
    },

    getListItemStyle(textOverlay) {
      return {
        top:
          (this.textOverlays.length - 1 - textOverlay.zIndex) *
            (this.totHeight + this.totMargin) +
          'px',
      };
    },

    getTotStyle(textOverlay) {
      const totStyle = this.totStyles[textOverlay.id];
      return {
        transform: `translateX(${totStyle.left}px)`,
        width: totStyle.width + 'px',
      };
    },

    getListItemClass(textOverlay) {
      return {
        '-dragging':
          this.interaction.id === textOverlay.id &&
          (this.horizontalAction || this.verticalAction),
        '-horizontal':
          this.interaction.id === textOverlay.id && this.horizontalAction,
        '-vertical':
          this.interaction.id === textOverlay.id && this.verticalAction,
        '-selected': this.activeTextOverlayId === textOverlay.id,
      };
    },

    onActiveTextOverlayId: function (activeTextOverlayId) {
      this.activeTextOverlayId = activeTextOverlayId;
    },

    movePlayhead: function (e) {
      let offset = e.clientX - this.$el.getBoundingClientRect().x;
      if (this.interaction.direction === 'left') {
        const snapPoint = this.getNextSnapPointToTheLeft(offset);
        if (snapPoint !== null && offset - snapPoint <= this.snapThreshold) {
          offset = snapPoint;
        }
      }
      if (this.interaction.direction === 'right') {
        const snapPoint = this.getNextSnapPointToTheRight(offset);
        if (snapPoint !== null && snapPoint - offset <= this.snapThreshold) {
          offset = snapPoint;
        }
      }
      offset = _.constrain(0, offset, app.ASSET_EDITOR_WIDTH);
      this.playheadPosition(this.leftPositionInPercent(offset));
      this.setTimeInAsset(this.leftPositionInPercent(offset));
    },

    renderOverlayMarkers() {
      const pixelsPerSecond = this.assetEditorWidth / this.displayDuration;
      this.overlayTrackMarkers = this.overlayTracks.map((t) => {
        const left = t.displayStartTime * pixelsPerSecond;
        const width =
          (t.displayDuration / this.displayDuration) * this.assetEditorWidth;
        return {
          left,
          width,
        };
      });
    },

    renderRuler() {
      const interval = Math.ceil(this.displayDuration / 10);
      let numSegments = Math.floor(this.displayDuration / interval);
      const pixelsPerSecond = this.assetEditorWidth / this.displayDuration;
      const pixelsPerSegment = pixelsPerSecond * interval;
      let tmpRuler;
      let startTime;
      let i;
      let lastSegmentWidth;

      for (i = 0; i < numSegments; i++) {
        startTime = app.seconds2MinutesDisplay(i * interval);
        tmpRuler = { startTime, width: pixelsPerSegment };
        this.ruler.push(tmpRuler);
      }
      startTime = app.seconds2MinutesDisplay(i * interval);
      const endTime = app.seconds2MinutesDisplay(this.displayDuration, true);
      if (
        ((this.displayDuration - i * interval) / this.displayDuration) *
          this.assetEditorWidth <=
        50
      ) {
        // if the width of the last segment is less than 20px, omit it
        numSegments--;
        lastSegmentWidth =
          this.assetEditorWidth - numSegments * pixelsPerSegment;
        tmpRuler.endTime = endTime;
        tmpRuler.width = lastSegmentWidth;
      } else {
        lastSegmentWidth =
          this.assetEditorWidth - numSegments * pixelsPerSegment;
        this.ruler.push({ startTime, endTime, width: lastSegmentWidth });
      }
    },

    leftPositionInPercent: function (leftPositionPixels) {
      return app.sfp(leftPositionPixels / this.assetEditorWidth, 1e4);
    },

    selectTextOverlay(textOverlay) {
      this.setTmpPageState({ totSelected: textOverlay });
    },

    onMouseDown(e) {
      if (e.button > 0) {
        return; // track LMB only
      }
      this.interaction.startX = e.clientX;
      this.interaction.startY = e.clientY;
      this.interaction.lastX = e.clientX;
      if (this.$el.contains(e.target)) {
        this.interaction.shouldMovePlayhead = true;
      }
      const el = e.target.closest('[data-tot-action]');
      if (el) {
        const action = el.dataset.totAction;
        if (action === 'move-play-head') {
          this.interaction.mode = action;
          this.snapPoints = this.createSnapPointsArray();
          return;
        }
        const id = el.dataset.textoverlayId;
        if (!id || !action) {
          return;
        }
        const totStyle = this.totStyles[id];
        if (!totStyle) {
          return;
        }
        this.interaction.id = id;
        this.interaction.originalLeft = totStyle.left;
        this.interaction.originalWidth = totStyle.width;
        this.interaction.mode = action;
        this.disableMouse();
        const textOverlay = this.getTextOverlayById(id);
        if (action === 'move-vertical' && this.textOverlays.length > 1) {
          document.body.style.cursor = 'grabbing';
          this.interaction.zIndex = textOverlay.zIndex;
          this.shadow.show = true;
          this.shadow.left = totStyle.left;
          this.shadow.width = totStyle.width;
          this.shadow.text = textOverlay._text;
          this.shadow.top =
            (this.textOverlays.length - 1 - textOverlay.zIndex) *
            (this.totHeight + this.totMargin);
          this.shadow.originalLeft = this.shadow.left;
          this.shadow.originalTop = this.shadow.top;
        }
        if (this.horizontalAction) {
          if (action === 'move-horizontal') {
            document.body.style.cursor = 'grabbing';
          } else {
            document.body.style.cursor = 'ew-resize';
          }
          this.snapPoints = this.createSnapPointsArray(true);
        }
        document.body.style.userSelect = 'none';
      }
    },

    createSnapPointsArray(includePlayhead = false) {
      const snapPoints = [];
      const textOverlaysIds = Object.keys(this.totStyles);
      for (const toId of textOverlaysIds) {
        if (toId !== this.interaction.id) {
          const currentToStyle = this.totStyles[toId];
          snapPoints.push(currentToStyle.left);
          snapPoints.push(currentToStyle.left + currentToStyle.width);
        }
      }
      if (includePlayhead) {
        snapPoints.push(this.playheadLeft);
      }
      snapPoints.sort();
      return snapPoints;
    },

    onMouseMove(e) {
      if (
        !this.horizontalAction &&
        !this.verticalAction &&
        this.interaction.mode !== 'move-play-head'
      ) {
        return;
      }
      if (e.clientX > this.interaction.lastX) {
        this.interaction.direction = 'right';
      }
      if (e.clientX < this.interaction.lastX) {
        this.interaction.direction = 'left';
      }
      this.interaction.lastX = e.clientX;

      if (this.interaction.mode === 'move-play-head') {
        this.movePlayhead(e);
      }
      if (this.interaction.mode === 'move-horizontal') {
        this.moveHorizontally(e);
      }
      if (this.interaction.mode === 'move-vertical') {
        this.moveVertically(e);
      }
      if (this.interaction.mode === 'resize-right') {
        this.resizeFromRight(e);
      }
      if (this.interaction.mode === 'resize-left') {
        this.resizeFromLeft(e);
      }
    },

    onMouseEnter(textOverlayUnderCursor) {
      if (
        this.interaction.mode !== 'move-vertical' ||
        textOverlayUnderCursor.id === this.interaction.id
      ) {
        return;
      }

      const newZIndexOfDraggedTo = textOverlayUnderCursor.zIndex;
      if (this.interaction.zIndex > newZIndexOfDraggedTo) {
        const textOverlaysToMove = this.getTextOverlayByZIndexRange(
          newZIndexOfDraggedTo,
          this.interaction.zIndex - 1
        );
        for (const t of textOverlaysToMove) {
          this.setTempTextOverlay({
            id: t.id,
            zIndex: t.zIndex + 1,
          });
        }
      }
      if (this.interaction.zIndex < newZIndexOfDraggedTo) {
        const textOverlaysToMove = this.getTextOverlayByZIndexRange(
          this.interaction.zIndex + 1,
          newZIndexOfDraggedTo
        );
        for (const t of textOverlaysToMove) {
          this.setTempTextOverlay({
            id: t.id,
            zIndex: t.zIndex - 1,
          });
        }
      }
      this.setTempTextOverlay({
        id: this.interaction.id,
        zIndex: newZIndexOfDraggedTo,
      });
      this.interaction.zIndex = newZIndexOfDraggedTo;
    },

    moveHorizontally(e) {
      if (this.interaction.direction === 'left') {
        this.moveLeft(e);
      }
      if (this.interaction.direction === 'right') {
        this.moveRight(e);
      }
    },

    moveLeft(e) {
      if (!this.currentTotStyle) {
        return;
      }
      const dx = e.clientX - this.interaction.startX;
      let left = this.interaction.originalLeft + dx;
      const maxLeft = app.ASSET_EDITOR_WIDTH - this.interaction.originalWidth;
      let snapSide = null;

      const nextSnapPointForLeftEdge = this.getNextSnapPointToTheLeft(left);
      if (nextSnapPointForLeftEdge !== null) {
        snapSide = 'left';
      }
      const nextSnapPointForRightEdge = this.getNextSnapPointToTheLeft(
        left + this.interaction.originalWidth
      );
      if (nextSnapPointForRightEdge !== null) {
        if (snapSide === null) {
          snapSide = 'right';
        } else {
          const delta1 = left - nextSnapPointForLeftEdge;
          const delta2 =
            left + this.interaction.originalWidth - nextSnapPointForRightEdge;
          if (delta2 < delta1) {
            snapSide = 'right';
          }
        }
      }

      if (snapSide === 'left') {
        if (left - nextSnapPointForLeftEdge <= this.snapThreshold) {
          left = nextSnapPointForLeftEdge;
        }
      } else if (snapSide === 'right') {
        if (
          left + this.interaction.originalWidth - nextSnapPointForRightEdge <=
          this.snapThreshold
        ) {
          left = nextSnapPointForRightEdge - this.interaction.originalWidth;
        }
      }
      left = left < 0 ? 0 : left;
      left = left > maxLeft ? maxLeft : left;
      this.currentTotStyle.left = left;
    },

    moveRight(e) {
      if (!this.currentTotStyle) {
        return;
      }

      const dx = e.clientX - this.interaction.startX;
      let left = this.interaction.originalLeft + dx;
      const maxLeft = app.ASSET_EDITOR_WIDTH - this.interaction.originalWidth;
      let snapSide = null;

      const nextSnapPointForLeftEdge = this.getNextSnapPointToTheRight(left);
      if (nextSnapPointForLeftEdge !== null) {
        snapSide = 'left';
      }
      const nextSnapPointForRightEdge = this.getNextSnapPointToTheRight(
        left + this.interaction.originalWidth
      );
      if (nextSnapPointForRightEdge !== null) {
        if (snapSide === null) {
          snapSide = 'right';
        } else {
          const delta1 = nextSnapPointForLeftEdge - left;
          const delta2 =
            nextSnapPointForRightEdge - (left + this.interaction.originalWidth);
          if (delta2 < delta1) {
            snapSide = 'right';
          }
        }
      }

      if (snapSide === 'left') {
        if (nextSnapPointForLeftEdge - left <= this.snapThreshold) {
          left = nextSnapPointForLeftEdge;
        }
      } else if (snapSide === 'right') {
        if (
          nextSnapPointForRightEdge - (left + this.interaction.originalWidth) <=
          this.snapThreshold
        ) {
          left = nextSnapPointForRightEdge - this.interaction.originalWidth;
        }
      }

      left = left < 0 ? 0 : left;
      left = left > maxLeft ? maxLeft : left;
      this.currentTotStyle.left = left;
    },

    getNextSnapPointToTheLeft(x) {
      const filtered = this.snapPoints.filter((p) => p < x);
      if (!filtered.length) {
        return null;
      }
      return Math.max(...filtered);
    },

    getNextSnapPointToTheRight(x) {
      const filtered = this.snapPoints.filter((p) => p > x);
      if (!filtered.length) {
        return null;
      }
      return Math.min(...filtered);
    },

    moveVertically(e) {
      const dy = e.clientY - this.interaction.startY;
      this.shadow.top = this.shadow.originalTop + dy;
      // this.shadow.left = this.shadow.originalLeft + dx;
      const listRect = this.$refs.ul.getBoundingClientRect();
      const listTop = listRect.y;
      const listBottom = listRect.height;
      this.shadow.top = _.constrain(-20, this.shadow.top, listBottom);
      let zIndexUnderCursor = Math.floor(
        (e.clientY - listTop) / (this.totHeight + this.totMargin)
      );
      zIndexUnderCursor = this.textOverlays.length - 1 - zIndexUnderCursor;
      if (zIndexUnderCursor <= 0) {
        zIndexUnderCursor = 0;
      }
      if (zIndexUnderCursor >= this.textOverlays.length) {
        zIndexUnderCursor = this.textOverlays.length - 1;
      }
      const positionInside = Math.floor(
        (e.clientY - listTop) % (this.totHeight + this.totMargin)
      );
      if (positionInside >= 0 && positionInside <= 20) {
        this.onMouseEnter(this.getTextOverlayByZindex(zIndexUnderCursor));
      }
    },

    resizeFromRight(e) {
      if (!this.currentTotStyle) {
        return;
      }
      let snapPoint = null;
      const dx = e.clientX - this.interaction.startX;
      let width = this.interaction.originalWidth + dx;

      if (this.interaction.direction === 'right') {
        snapPoint = this.getNextSnapPointToTheRight(
          this.interaction.originalLeft + width
        );
        if (
          snapPoint !== null &&
          snapPoint - (this.interaction.originalLeft + width) <=
            this.snapThreshold
        ) {
          width = snapPoint - this.interaction.originalLeft;
        }
      } else if (this.interaction.direction === 'left') {
        snapPoint = this.getNextSnapPointToTheLeft(
          this.interaction.originalLeft + width
        );
        if (
          snapPoint !== null &&
          this.interaction.originalLeft + width - snapPoint <=
            this.snapThreshold
        ) {
          width = snapPoint - this.interaction.originalLeft;
        }
      }

      const maxWidth = app.ASSET_EDITOR_WIDTH - this.interaction.originalLeft;
      width = _.constrain(this.minTotWidth, width, maxWidth);
      this.currentTotStyle.width = width;
    },

    resizeFromLeft(e) {
      if (!this.currentTotStyle) {
        return;
      }
      let snapPoint = null;
      const dx = e.clientX - this.interaction.startX;
      const maxLeft =
        this.interaction.originalLeft +
        this.interaction.originalWidth -
        this.minTotWidth;
      let left = this.interaction.originalLeft + dx;

      if (this.interaction.direction === 'right') {
        snapPoint = this.getNextSnapPointToTheRight(left);
        if (snapPoint !== null && snapPoint - left <= this.snapThreshold) {
          left = snapPoint;
        }
      } else if (this.interaction.direction === 'left') {
        snapPoint = this.getNextSnapPointToTheLeft(left);
        if (snapPoint !== null && left - snapPoint <= this.snapThreshold) {
          left = snapPoint;
        }
      }

      left = _.constrain(0, left, maxLeft);
      this.currentTotStyle.left = left;
      this.currentTotStyle.width =
        this.interaction.originalWidth + (this.interaction.originalLeft - left);
    },

    onMouseUp(e) {
      this.enableMouse();
      document.body.style.cursor = 'auto';
      document.body.style.userSelect = '';
      if (this.interaction.shouldMovePlayhead) {
        if (
          this.interaction.mode === 'none' ||
          e.clientX === this.interaction.startX
        ) {
          this.movePlayhead(e);
        }
        this.interaction.shouldMovePlayhead = false;
      }
      this.setStartAndEndTime();
      this.interaction.mode = 'none';
      this.interaction.startX = 0;
      this.interaction.startY = 0;
      this.interaction.lastX = 0;
      this.interaction.originalLeft = 0;
      this.interaction.originalWidth = 0;
      this.interaction.id = null;
      this.shadow.top = 0;
      this.shadow.width = 0;
      this.shadow.left = 0;
      this.shadow.text = '';
      this.shadow.show = false;
      this.snapPoints = [];
    },

    setStartAndEndTime() {
      const totStyle = this.totStyles[this.interaction.id];
      if (!totStyle) {
        return;
      }
      const animationStartTime = app.sfp(
        totStyle.left / app.ASSET_EDITOR_WIDTH,
        1e4
      );
      const animationEndTime = app.sfp(
        (totStyle.left + totStyle.width) / app.ASSET_EDITOR_WIDTH,
        1e4
      );
      this.setTempTextOverlay({
        id: this.interaction.id,
        animationStartTime,
        animationEndTime,
      });
    },

    onAddTextOverlay(to) {
      const { left, width } = this.calcTotStyle(to);
      app.Vue.set(this.totStyles, to.id, { left, width });
      this.$nextTick(() => {
        this.$refs.ul
          .querySelector('li:last-child')
          .scrollIntoView({ behavior: 'smooth' });
      });
    },

    onDeleteTextOverlay(to) {
      const tos = this.getTextOverlayByZIndexRange(to.zIndex + 1);
      if (!tos || !tos.length) {
        return;
      }
      for (const to of tos) {
        this.setTempTextOverlay({
          id: to.id,
          zIndex: to.zIndex - 1,
        });
      }
    },
  },

  created: function () {
    this.renderRuler();
    this.renderOverlayMarkers();
  },

  beforeMount() {
    if (!this.textOverlays || !this.textOverlays.length) {
      return;
    }
    const clone = _.cloneDeep(this.textOverlays);
    clone.sort((to1, to2) => to1.zIndex - to2.zIndex);

    for (let i = 0; i < clone.length; i++) {
      const { left, width } = this.calcTotStyle(clone[i]);
      this.setTempTextOverlay({
        id: clone[i].id,
        zIndex: i,
      });
      app.Vue.set(this.totStyles, clone[i].id, { left, width });
    }
  },

  mounted: function () {
    this.totHeight = parseInt(
      app.cssVarValue(this.$refs.ul, '--timeline-height')
    );
    this.totMargin = parseInt(
      app.cssVarValue(this.$refs.ul, '--timeline-margin')
    );
    this.setTimeInAssetToFirstTextOverlay();
    this.playheadPositionToFirstTextOverlay();
    bus.$on('to:activeTextOverlayId', this.onActiveTextOverlayId);
    bus.$on('to:added', this.onAddTextOverlay);
    bus.$on('to:deleted', this.onDeleteTextOverlay);

    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mousemove', this.onMouseMove);
  },

  beforeDestroy: function () {
    bus.$off('to:activeTextOverlayId', this.onActiveTextOverlayId);
    bus.$off('to:added', this.onAddTextOverlay);
    bus.$off('to:deleted', this.onDeleteTextOverlay);

    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mousemove', this.onMouseMove);
  },
};
</script>
